import { ValidationRule } from '../../components/custom-input/custom-input.component';
import { EmailValidator } from './email.validator';
import { PhoneValidator } from './phone.validator';

export const CUSTOMER_NAME_MAX_LENGTH = 50;
/** Max digits for a US customer phone (NANP). */
export const CUSTOMER_PHONE_MAX_DIGITS = 10;
/** Max length for digits-only phone inputs (Orders / Appointments / Customers). */
export const CUSTOMER_PHONE_MAX_LENGTH = CUSTOMER_PHONE_MAX_DIGITS;
/** Max length when formatting characters are allowed (e.g. prepaid redemption). */
export const CUSTOMER_PHONE_FORMATTED_MAX_LENGTH = 14;
export const CUSTOMER_EMAIL_MAX_LENGTH = 254;

const CUSTOMER_NAME_PATTERN = /^[a-zA-Z\s'-]+$/;

/** Digits only, capped at 10 — used by Order Request, Schedule Appointment, Add Customer. */
export function sanitizeCustomerPhoneInput(value: string): string {
  return String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, CUSTOMER_PHONE_MAX_DIGITS);
}

/**
 * Allow digits plus US formatting characters `() . -` and spaces, still capped at 10 digits.
 * Used by prepaid redemption inputs that display formatted numbers.
 */
export function sanitizeCustomerPhoneInputAllowFormatting(value: string): string {
  const cleaned = String(value ?? '').replace(/[^\d\s().-]/g, '');
  let digitCount = 0;
  const chars: string[] = [];
  for (const ch of cleaned) {
    if (/\d/.test(ch)) {
      if (digitCount >= CUSTOMER_PHONE_MAX_DIGITS) {
        continue;
      }
      digitCount += 1;
    }
    chars.push(ch);
  }

  // Digits are already capped; only then shrink formatting to the display max.
  while (chars.length > CUSTOMER_PHONE_FORMATTED_MAX_LENGTH) {
    const firstDigitIdx = chars.findIndex(c => /\d/.test(c));
    if (firstDigitIdx > 0) {
      chars.splice(0, 1);
      continue;
    }
    if (chars.length > 0 && !/\d/.test(chars[chars.length - 1]!)) {
      chars.pop();
      continue;
    }
    const nonDigitIdx = chars.findIndex(c => !/\d/.test(c));
    if (nonDigitIdx === -1) {
      break;
    }
    chars.splice(nonDigitIdx, 1);
  }
  return chars.join('');
}

export function sanitizeCustomerNameInput(value: string): string {
  return String(value ?? '')
    .replace(/[^a-zA-Z\s'-]/g, '')
    .slice(0, CUSTOMER_NAME_MAX_LENGTH);
}

export function sanitizeCustomerEmailInput(value: string): string {
  return String(value ?? '').slice(0, CUSTOMER_EMAIL_MAX_LENGTH);
}

export function validateCustomerName(value: string): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.length > CUSTOMER_NAME_MAX_LENGTH) {
    return 'Name must be 50 characters or less';
  }
  if (!CUSTOMER_NAME_PATTERN.test(trimmed)) {
    return 'Name may only include letters, spaces, hyphens, and apostrophes';
  }
  if (trimmed.length < 2) {
    return 'Name must be at least 2 characters long';
  }
  return '';
}

export function validateCustomerPhone(value: string): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  const result = PhoneValidator.usaPhone({ value: trimmed } as any);
  return result ? result['usaPhone']?.message || 'Please enter a valid phone number' : '';
}

export function validateCustomerEmail(value: string): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.length > CUSTOMER_EMAIL_MAX_LENGTH) {
    return 'Email must be 254 characters or less';
  }
  const result = EmailValidator.strictEmail({ value: trimmed } as any);
  return result
    ? (result['strictEmail']?.message || 'Please enter a valid email address').trim()
    : '';
}

export const CUSTOMER_FIRST_NAME_VALIDATION_RULES: ValidationRule[] = [
  {
    type: 'maxLength',
    value: CUSTOMER_NAME_MAX_LENGTH,
    message: 'First name must be 50 characters or less',
  },
  {
    type: 'custom',
    value: (v: string) => {
      const t = (v ?? '').trim();
      return !t || CUSTOMER_NAME_PATTERN.test(t);
    },
    message: 'First name may only include letters, spaces, hyphens, and apostrophes',
  },
];

export const CUSTOMER_LAST_NAME_VALIDATION_RULES: ValidationRule[] = [
  {
    type: 'maxLength',
    value: CUSTOMER_NAME_MAX_LENGTH,
    message: 'Last name must be 50 characters or less',
  },
  {
    type: 'custom',
    value: (v: string) => {
      const t = (v ?? '').trim();
      return !t || CUSTOMER_NAME_PATTERN.test(t);
    },
    message: 'Last name may only include letters, spaces, hyphens, and apostrophes',
  },
];

export const CUSTOMER_PHONE_VALIDATION_RULES: ValidationRule[] = [
  {
    type: 'custom',
    value: (v: string) => {
      const t = (v ?? '').trim();
      return !t || PhoneValidator.usaPhone({ value: t } as any) === null;
    },
    message: (v: string) => validateCustomerPhone(v),
  },
];

export const CUSTOMER_EMAIL_VALIDATION_RULES: ValidationRule[] = [
  {
    type: 'maxLength',
    value: CUSTOMER_EMAIL_MAX_LENGTH,
    message: 'Email must be 254 characters or less',
  },
  {
    type: 'custom',
    value: (v: string) => {
      const t = (v ?? '').trim();
      return !t || EmailValidator.strictEmail({ value: t } as any) === null;
    },
    message: (v: string) => validateCustomerEmail(v),
  },
];
